import type Highcharts from 'highcharts';
import type { TFunction } from 'i18next';
import type { LiquidityCashFlowType } from '../../app/api/generated/client';
import { formatAmountCurrencyFirst } from '../../app/utils/renderUtils';

// Gap between the tooltip label and the point / the visible plot edges.
const TOOLTIP_DISTANCE_PX = 16;

/**
 * Positions the tooltip so it stays inside the visible plot window and its callout arrow keeps pointing at the column.
 *
 * The plot scrolls, and Highcharts' default placement clamps to the *full* scrollable width — at the outer columns that
 * clips the label in the scrolled-away area or under the fixed y-axis. So every branch keeps the label between the fixed
 * y-axis and the visible right edge (accounting for the scroll offset).
 *
 * Placement: above the point (bottom arrow) when the label fits above the plot; otherwise beside it, centred vertically
 * — right side first (while it fits before the visible right edge), then left, then clamped to the roomier side. Side
 * placement exists because the `outside: false` tooltip can't leave the short modal chart, so a tall label (a dense
 * month) can't sit above a mid-plot point without being clipped.
 */
export const liquidityNeedTooltipPositioner: Highcharts.TooltipPositionerCallbackFunction = function (this, labelWidth, labelHeight, point) {
    const chart = this.chart;
    const scrolling = (chart as Highcharts.Chart & { scrollablePlotArea?: { scrollingContainer?: HTMLElement } }).scrollablePlotArea?.scrollingContainer;
    const scrollLeft = scrolling?.scrollLeft ?? 0;
    const visibleRight = scrolling ? scrollLeft + scrolling.clientWidth : chart.chartWidth;

    const pointX = chart.plotLeft + (point.plotX ?? 0);
    const pointY = chart.plotTop + (point.plotY ?? 0);

    // Horizontal window: right of the fixed y-axis, left of the visible right edge (both in scrolled plot coordinates).
    const minX = scrollLeft + chart.plotLeft;
    const maxX = Math.max(minX, visibleRight - labelWidth - TOOLTIP_DISTANCE_PX);

    // Vertical window: keep the whole label inside the plot. Math.max keeps a label taller than the plot top-aligned.
    const minY = chart.plotTop;
    const maxY = Math.max(minY, chart.plotTop + chart.plotHeight - labelHeight);

    // Preferred: above the point, centred and clamped into the window (bottom arrow).
    const aboveY = pointY - labelHeight - TOOLTIP_DISTANCE_PX;
    if (aboveY >= minY) {
        const x = Math.min(Math.max(pointX - labelWidth / 2, minX), maxX);

        return { x, y: aboveY };
    }

    // No room above: beside the point, centred vertically (side arrow).
    const y = Math.min(Math.max(pointY - labelHeight / 2, minY), maxY);
    const rightX = pointX + TOOLTIP_DISTANCE_PX;
    const leftX = pointX - TOOLTIP_DISTANCE_PX - labelWidth;

    if (rightX <= maxX) {
        return { x: rightX, y };
    }
    if (leftX >= minX) {
        return { x: leftX, y };
    }

    // Neither side fits fully; use the side with more room and clamp so the label stays inside the visible window.
    const x = visibleRight - pointX >= pointX - minX ? Math.min(rightX, maxX) : Math.max(leftX, minX);

    return { x, y };
};

// A plain styled <span> replicating <zf-pill size="small">, using the same zafire tokens. We can't use the
// real <zf-pill> here: it is a Stencil web component that renders its shadow DOM asynchronously, so when
// Highcharts measures the useHTML tooltip (via offsetWidth, once, on refresh) the pill is still empty and the
// callout box gets sized too small, clipping the content.
const pill = (type: NonNullable<LiquidityCashFlowType>, text: string) => {
    const tone = type === 'RECURRING' ? 'success' : 'info';

    return `<span style="display: inline-flex; align-items: center; padding: 0 6px; border-radius: var(--zf-pill-radius); color: var(--fg-color-${tone}-strong); background-color: var(--bg-color-${tone}-normal); font-size: var(--type-label-extra-small-font-size); line-height: var(--type-label-extra-small-line-height); font-weight: var(--type-label-extra-small-regular-font-weight);">${text}</span>`;
};

type TooltipCashFlow = {
    type?: LiquidityCashFlowType;
    name?: string;
    amount?: number;
};

type TooltipCustom = {
    date?: string;
    amount?: number;
    balance?: number;
    cashFlows?: TooltipCashFlow[];
};

// Minimal shapes of the Highcharts objects the month lookup reaches into; kept narrow so it tolerates missing
// runtime fields (e.g. in tests).
type PointLike = {
    x?: number;
    y?: number | null;
    options?: { custom?: TooltipCustom };
};

type SeriesLike = {
    type?: string;
    options?: { id?: string };
    points?: PointLike[];
};

// The full data for one month, assembled from both series so a single tooltip can always show the cash-flow
// breakdown (from the column) together with the liquidity need (from the line).
type MonthData = {
    columnCustom?: TooltipCustom;
    needValue: number;
};

// formatAmountCurrencyFirst returns '' for a zero/falsy value; in the need graph a zero cash-flow, month total or
// balance must still read as "<currency> 0" rather than blank, so we handle zero explicitly here.
const formatTooltipAmount = (value: number | undefined, currency: string, showPlusMinus = true): string => {
    const amount = value ?? 0;
    if (!amount) return [currency, '0'].filter(Boolean).join(' ');

    return formatAmountCurrencyFirst(amount, showPlusMinus, currency);
};

// Assembles the hovered month's full data from both series, so the tooltip is identical whether the cursor snapped to
// the column or the line. The column's cash-flow breakdown and the line's need value each come from their series' point
// at the hovered month; when the sibling series can't be reached (e.g. in tests) it falls back to the hovered point.
const resolveMonthData = (context: ThisParameterType<Highcharts.TooltipFormatterCallbackFunction>): MonthData => {
    const hoveredCustom = context.options?.custom as TooltipCustom | undefined;
    const hoveredIsLine = context.series?.type === 'line';

    let columnCustom = hoveredIsLine ? undefined : hoveredCustom;
    let needValue = hoveredIsLine ? ((context.y ?? 0) as number) : 0;

    const allSeries = context.series?.chart?.series as SeriesLike[] | undefined;
    if (allSeries) {
        const columnSeries = allSeries.find((series) => series.type === 'columnrange');
        const lineSeries = allSeries.find((series) => series.options?.id === 'liquidityNeed');
        const columnPoint = columnSeries?.points?.find((point) => point.x === context.x);
        const linePoint = lineSeries?.points?.find((point) => point.x === context.x);

        if (columnPoint?.options?.custom) columnCustom = columnPoint.options.custom;
        if (linePoint) needValue = linePoint.y ?? 0;
    }

    return { columnCustom, needValue };
};

// Inline styles for the useHTML tooltip (Highcharts renders a raw HTML string), kept at module scope so the markup is
// defined once and createTooltipFormatter reads as *what* to show rather than a wall of style attributes.
const TOOLTIP_STYLE = 'display: flex; flex-direction: column; align-items: stretch; gap: 8px; min-width: 320px;';
const DATE_STYLE =
    'color: var(--fg-color-default-muted); font-size: var(--type-label-extra-small-font-size); line-height: var(--type-label-extra-small-line-height);';
const LABEL_STYLE = 'color: var(--fg-color-default-emphasis); font-size: var(--type-label-small-font-size); line-height: var(--type-label-small-line-height);';
const AMOUNT_STYLE =
    'color: var(--fg-color-default-normal); font-size: var(--type-body-small-font-size); line-height: var(--type-body-small-line-height); white-space: nowrap;';
const ENTRY_NAME_STYLE = `flex: 1 1 auto; min-width: 0; ${LABEL_STYLE}`;
const ENTRY_ROW_STYLE = 'display: flex; align-items: center; gap: 12px;';
const SUMMARY_ROW_STYLE = 'display: flex; align-items: center; justify-content: space-between; gap: 16px;';
const ROW_DIVIDER_STYLE = 'padding-top: 4px; border-top: 1px solid var(--border-color-default-normal);';

const span = (style: string, content: string) => `<span style="${style}">${content}</span>`;
const amountSpan = (amount: string) => span(AMOUNT_STYLE, `<b>${amount}</b>`);
const wrap = (rows: string) => `<div style="${TOOLTIP_STYLE}">${rows}</div>`;

// A cash-flow entry: name (fills the row), amount, and an optional type pill.
const entryRow = (name: string, amount: string, pillPart = '') =>
    `<div style="${ENTRY_ROW_STYLE}">${span(ENTRY_NAME_STYLE, name)}${amountSpan(amount)}${pillPart}</div>`;

// A label/amount summary row (month net, balance, liquidity need), optionally preceded by a divider and with a bold
// label to emphasise it (the amount is always bold).
const summaryRow = (label: string, amount: string, { divided = false, bold = false }: { divided?: boolean; bold?: boolean } = {}) =>
    `<div style="${SUMMARY_ROW_STYLE}${divided ? ` ${ROW_DIVIDER_STYLE}` : ''}">${span(LABEL_STYLE, bold ? `<b>${label}</b>` : label)}${amountSpan(amount)}</div>`;

// The liquidity-need summary row, divided from the cash-flow block above it and bold to emphasise it.
const liquidityNeedRow = (t: TFunction, currency: string, needValue: number) =>
    summaryRow(t('liquidityNeedDetailsGraph.series.liquidityNeed'), formatTooltipAmount(Math.abs(needValue), currency, false), { divided: true, bold: true });

// The cash-flow block: a row per entry (or a "No cashflows" row for an empty month), then the month net and balance.
const cashFlowRows = (t: TFunction, currency: string, custom: TooltipCustom | undefined) => {
    const cashFlows = custom?.cashFlows ?? [];

    const entryRows = cashFlows.length
        ? cashFlows
              .map((cashFlow) => {
                  const pillPart = cashFlow.type ? pill(cashFlow.type, t(`liquidityNeedDetailsGraph.series.cashflow-${cashFlow.type}`)) : '';

                  return entryRow(cashFlow.name ?? '', formatTooltipAmount(cashFlow.amount, currency), pillPart);
              })
              .join('')
        : entryRow(t('liquidityNeedDetailsGraph.series.noCashflows'), formatTooltipAmount(0, currency));

    const totalRow = summaryRow(t('liquidityNeedDetailsGraph.series.monthNet'), formatTooltipAmount(custom?.amount, currency), { divided: true });
    const balanceRow = summaryRow(t('liquidityNeedDetailsGraph.series.balance'), formatTooltipAmount(custom?.balance, currency));

    return entryRows + totalRow + balanceRow;
};

export const createTooltipFormatter = (t: TFunction, currency: string): Highcharts.TooltipFormatterCallbackFunction =>
    function (this) {
        const { columnCustom, needValue } = resolveMonthData(this);
        const hoveredCustom = this.options.custom as TooltipCustom | undefined;
        const date = columnCustom?.date ?? hoveredCustom?.date;
        const datePart = date ? span(DATE_STYLE, date) : '';

        return wrap(datePart + cashFlowRows(t, currency, columnCustom) + liquidityNeedRow(t, currency, needValue));
    };
